import { Box, Button, IconButton, Typography } from '@mui/material';
import { Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CustomCellRendererProps } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useDeleteUser } from '../hooks/api/use-users.hook';
import { UserApiModel, UserRelationship, WithDomains, WithRoles } from '../../../types/models/user.model';
import { useConfirm } from '../contexts/confirm.context';
import { InfniteScrollingGrid } from '../components/AgGrid/InfiniteScrollingGrid';
import { searchUsers } from '../api/users.api';

export const Users = () => {
  const navigate = useNavigate();
  const [showConfirm] = useConfirm();

  const [deleteUser, { loading: deletingUser, error: deleteError }] = useDeleteUser();

  const columns: ColDef<UserApiModel & WithRoles & WithDomains>[] = [
    {
      field: 'firstName',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'lastName',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'email',
      filter: true,
      sortable: true,
      filterParams: { filterOptions: ['contains'], maxNumConditions: 1 },
    },
    {
      field: 'roles',
      valueGetter: (params) => (params.data?.roles ?? []).map(({ name }) => name).join(', '),
    },
    {
      field: 'domains',
      valueGetter: (params) => (params.data?.domains ?? []).map(({ shortName }) => shortName).join(', '),
    },
    {
      headerName: 'Actions',
      maxWidth: 200,
      cellRenderer: ({ api, node }: CustomCellRendererProps<UserApiModel>) => (
        <>
          <IconButton onClick={() => navigate(`edit/${node.id}`)}>
            <EditIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              showConfirm({
                onConfirm: async () => {
                  if (node.id) {
                    await deleteUser({ userId: +node.id });
                    api.refreshInfiniteCache();
                  }
                },
                title: 'Delete User',
                message: `Are you sure you want to delete user "${node.data?.firstName} ${node.data?.lastName}"?`,
                error: deleteError?.response?.data?.message,
              });
            }}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ),
    },
  ];

  const getUsers: Parameters<typeof InfniteScrollingGrid<UserApiModel>>[0]['onGetRows'] = async ({
    cursor,
    limit,
    filterQuery,
    sortQuery,
  }) => {
    const { data } = await searchUsers({
      ...filterQuery,
      include: [UserRelationship.ROLES, UserRelationship.DOMAINS],
      cursor,
      limit,
      sort: sortQuery,
    });
    return data;
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title={'Users'} />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }}>
          <Typography variant="h5">Manage Users</Typography>
          <Box flexGrow={1} />
          <Button color="primary" variant="contained" startIcon={<AddIcon />} onClick={() => navigate(`add`)}>
            Add User
          </Button>
        </Box>

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          <InfniteScrollingGrid loading={deletingUser} columnDefs={columns} onGetRows={getUsers} />
        </Box>
      </Box>
    </Box>
  );
};
